/**
 * Supervisor app copy, English and Bengali.
 *
 * The standard room-item names deliberately stay in English in both locales:
 * they are inventory identifiers management reads on a report, not UI prose.
 */

export type Language = 'en' | 'bn';

export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'bn', label: 'বাংলা' },
];

type Copy = {
  /* shell */
  appName: string;
  tagline: string;
  language: string;
  signOut: string;
  back: string;
  cancel: string;
  close: string;
  retry: string;
  continueLabel: string;

  /* login */
  signIn: string;
  signInSubtitle: string;
  username: string;
  password: string;
  usernamePlaceholder: string;
  passwordPlaceholder: string;
  signingIn: string;
  showPassword: string;
  hidePassword: string;

  /* home */
  greeting: (name: string) => string;
  chooseInspection: string;
  checkoutRooms: string;
  checkoutHint: string;
  cleanRooms: string;
  cleanHint: string;
  myWorkToday: string;
  totalCompleted: string;
  checkout: string;
  clean: string;
  noWorkTitle: string;
  noWorkText: string;
  completedAt: (time: string) => string;
  resumeTitle: string;
  resumeText: (room: string) => string;
  resumeAction: string;
  discardAction: string;

  /* room entry */
  roomNumber: string;
  roomNumberHint: string;
  enterRoomNumber: string;
  invalidRoomNumber: string;
  confirmTitle: string;
  startInspection: string;
  starting: string;
  kitchenRoomNote: string;
  photosRequired: (count: number) => string;

  /* capture */
  photos: string;
  photosProgress: (done: number, total: number) => string;
  sectionRoomNumber: string;
  sectionBathroom: string;
  sectionKitchen: string;
  sectionRoom: string;
  sectionRoomNumberHint: string;
  sectionBathroomHint: string;
  sectionKitchenHint: string;
  sectionRoomHint: string;
  openCamera: string;
  capture: string;
  retake: string;
  usePhoto: string;
  uploading: string;
  retrying: string;
  uploaded: string;
  cameraStarting: string;
  cameraDeniedTitle: string;
  cameraDeniedText: string;
  cameraMissingTitle: string;
  cameraMissingText: string;
  cameraInsecureTitle: string;
  cameraInsecureText: string;
  cameraUnavailableTitle: string;
  cameraUnavailableText: string;
  cameraRecovering: string;
  restartCamera: string;
  photoSlot: (n: number) => string;
  allPhotosDone: string;
  nextSection: string;
  reviewItems: string;

  /* room items */
  roomItems: string;
  roomItemsQuestion: string;
  everythingComplete: string;
  everythingCompleteHint: string;
  missingItems: string;
  missingItemsHint: string;
  selectMissingItems: string;
  otherItem: string;
  otherItemPlaceholder: string;
  missingItemsEmpty: string;
  completeInspection: string;
  completing: string;

  /* success */
  inspectionCompleted: string;
  reportSent: string;
  checkAnotherRoom: string;
  durationLabel: string;

  /* errors */
  errSessionExpired: string;
  errSessionExpiredHelp: string;
  errInvalidCredentials: string;
  errNetwork: string;
  errNetworkPhoto: string;
  errServer: string;
  errTooLarge: string;
  errUnsupported: string;
  errLocked: string;
  errPhotosIncomplete: string;
  errForbidden: string;
  errUnknown: string;
  signInAgain: string;
  sessionKeptSafe: string;
};

const en: Copy = {
  appName: 'ROOM VERIFY',
  tagline: 'Room inspection evidence',
  language: 'Language',
  signOut: 'Sign out',
  back: 'Back',
  cancel: 'Cancel',
  close: 'Close',
  retry: 'Try again',
  continueLabel: 'Continue',

  signIn: 'Sign in',
  signInSubtitle: 'Use the account your manager gave you.',
  username: 'Username',
  password: 'Password',
  usernamePlaceholder: 'your username',
  passwordPlaceholder: 'your password',
  signingIn: 'Signing in…',
  showPassword: 'Show password',
  hidePassword: 'Hide password',

  greeting: (name) => `Hello, ${name}`,
  chooseInspection: 'What are you inspecting?',
  checkoutRooms: 'Checkout Rooms',
  checkoutHint: 'After a guest has left the room',
  cleanRooms: 'Clean Rooms',
  cleanHint: 'After cleaning has been completed',
  myWorkToday: 'My Work Today',
  totalCompleted: 'Total completed',
  checkout: 'Checkout',
  clean: 'Clean',
  noWorkTitle: 'No rooms yet today',
  noWorkText: 'Rooms you finish today will be listed here.',
  completedAt: (time) => `Completed ${time}`,
  resumeTitle: 'Unfinished inspection',
  resumeText: (room) => `You still have Room ${room} open.`,
  resumeAction: 'Resume',
  discardAction: 'Start something else',

  roomNumber: 'Room Number',
  roomNumberHint: '3 or 4 digits, as shown on the door',
  enterRoomNumber: 'Enter room number',
  invalidRoomNumber: 'Enter a valid room number (3 or 4 digits).',
  confirmTitle: 'Confirm before you start',
  startInspection: 'Start Inspection',
  starting: 'Starting…',
  kitchenRoomNote: 'This room has a kitchen — an extra photo section is required.',
  photosRequired: (count) => `${count} photos required`,

  photos: 'Photos',
  photosProgress: (done, total) => `${done} / ${total} photos`,
  sectionRoomNumber: 'Room Number',
  sectionBathroom: 'Bathroom',
  sectionKitchen: 'Kitchen',
  sectionRoom: 'Room',
  sectionRoomNumberHint: 'Photograph the room number on the door, from outside.',
  sectionBathroomHint: 'Four different views of the bathroom.',
  sectionKitchenHint: 'Four different views of the kitchen.',
  sectionRoomHint: 'Six different views of the room.',
  openCamera: 'Open Camera',
  capture: 'Capture',
  retake: 'Retake',
  usePhoto: 'Use Photo',
  uploading: 'Uploading…',
  retrying: 'Retrying…',
  uploaded: 'Uploaded',
  cameraStarting: 'Starting camera…',
  cameraDeniedTitle: 'Camera permission needed',
  cameraDeniedText:
    'ROOM VERIFY needs the camera to record inspection evidence. Allow camera access in your browser settings, then try again.',
  cameraMissingTitle: 'No camera found',
  cameraMissingText: 'This device has no usable camera. Inspections need a working camera.',
  cameraInsecureTitle: 'Secure connection required',
  cameraInsecureText: 'The camera only works over HTTPS. Open the app using its https:// address.',
  cameraUnavailableTitle: 'Camera unavailable',
  cameraUnavailableText:
    'The camera did not start properly. This can happen after a call, or after the phone has been locked. Tap Restart Camera to try again.',
  cameraRecovering: 'Restarting the camera…',
  restartCamera: 'Restart Camera',
  photoSlot: (n) => `Photo ${n}`,
  allPhotosDone: 'All photos captured',
  nextSection: 'Next section',
  reviewItems: 'Continue to Room Items',

  roomItems: 'Room Items',
  roomItemsQuestion: 'Are all room items complete?',
  everythingComplete: 'Everything is complete',
  everythingCompleteHint: 'Nothing is missing from the room.',
  missingItems: 'Missing items',
  missingItemsHint: 'Something is missing — choose what.',
  selectMissingItems: 'Select every missing item',
  otherItem: 'Other',
  otherItemPlaceholder: 'Write another missing item',
  missingItemsEmpty: 'Choose at least one missing item, or write one under Other.',
  completeInspection: 'Complete Inspection',
  completing: 'Completing…',

  inspectionCompleted: 'Inspection Completed',
  reportSent: 'Report sent to management',
  checkAnotherRoom: 'Check Another Room',
  durationLabel: 'Duration',

  errSessionExpired: 'Your session expired',
  errSessionExpiredHelp: 'Sign in again to continue where you left off.',
  errInvalidCredentials: 'Wrong username or password.',
  errNetwork: 'Connection problem. Check your internet and try again.',
  errNetworkPhoto:
    'The photo could not be uploaded because the connection is unstable. The photo is still saved on this screen — tap Use Photo to retry.',
  errServer: 'The server had a temporary problem. Please try again in a moment.',
  errTooLarge: 'That photo is too large. Take it again.',
  errUnsupported: 'That image format is not supported. Take the photo again.',
  errLocked: 'This inspection is already completed.',
  errPhotosIncomplete: 'Some required photos are still missing.',
  errForbidden: 'This account cannot do that.',
  errUnknown: 'Something went wrong. Please try again.',
  signInAgain: 'Sign in again',
  sessionKeptSafe: 'Your inspection is saved. Nothing is lost.',
};

const bn: Copy = {
  appName: 'ROOM VERIFY',
  tagline: 'রুম পরিদর্শনের প্রমাণ',
  language: 'ভাষা',
  signOut: 'সাইন আউট',
  back: 'পিছনে',
  cancel: 'বাতিল',
  close: 'বন্ধ',
  retry: 'আবার চেষ্টা করুন',
  continueLabel: 'চালিয়ে যান',

  signIn: 'সাইন ইন',
  signInSubtitle: 'ম্যানেজারের দেওয়া অ্যাকাউন্ট ব্যবহার করুন।',
  username: 'ইউজারনেম',
  password: 'পাসওয়ার্ড',
  usernamePlaceholder: 'আপনার ইউজারনেম',
  passwordPlaceholder: 'আপনার পাসওয়ার্ড',
  signingIn: 'সাইন ইন হচ্ছে…',
  showPassword: 'পাসওয়ার্ড দেখান',
  hidePassword: 'পাসওয়ার্ড লুকান',

  greeting: (name) => `স্বাগতম, ${name}`,
  chooseInspection: 'আপনি কী পরিদর্শন করছেন?',
  checkoutRooms: 'চেকআউট রুম',
  checkoutHint: 'অতিথি রুম ছেড়ে যাওয়ার পরে',
  cleanRooms: 'ক্লিন রুম',
  cleanHint: 'পরিষ্কার করার কাজ শেষ হওয়ার পরে',
  myWorkToday: 'আজকের আমার কাজ',
  totalCompleted: 'মোট সম্পন্ন',
  checkout: 'চেকআউট',
  clean: 'ক্লিন',
  noWorkTitle: 'আজ এখনও কোনো রুম হয়নি',
  noWorkText: 'আজ যে রুমগুলো শেষ করবেন সেগুলো এখানে দেখা যাবে।',
  completedAt: (time) => `সম্পন্ন ${time}`,
  resumeTitle: 'অসমাপ্ত পরিদর্শন',
  resumeText: (room) => `রুম ${room} এখনও খোলা আছে।`,
  resumeAction: 'চালিয়ে যান',
  discardAction: 'অন্য কিছু শুরু করুন',

  roomNumber: 'রুম নম্বর',
  roomNumberHint: 'দরজায় লেখা ৩ বা ৪ সংখ্যার নম্বর',
  enterRoomNumber: 'রুম নম্বর লিখুন',
  invalidRoomNumber: 'সঠিক রুম নম্বর লিখুন (৩ বা ৪ সংখ্যা)।',
  confirmTitle: 'শুরু করার আগে নিশ্চিত করুন',
  startInspection: 'পরিদর্শন শুরু করুন',
  starting: 'শুরু হচ্ছে…',
  kitchenRoomNote: 'এই রুমে রান্নাঘর আছে — অতিরিক্ত ছবির সেকশন লাগবে।',
  photosRequired: (count) => `${count} টি ছবি প্রয়োজন`,

  photos: 'ছবি',
  photosProgress: (done, total) => `${done} / ${total} ছবি`,
  sectionRoomNumber: 'রুম নম্বর',
  sectionBathroom: 'বাথরুম',
  sectionKitchen: 'রান্নাঘর',
  sectionRoom: 'রুম',
  sectionRoomNumberHint: 'বাইরে থেকে দরজার রুম নম্বরের ছবি তুলুন।',
  sectionBathroomHint: 'বাথরুমের চারটি আলাদা ছবি।',
  sectionKitchenHint: 'রান্নাঘরের চারটি আলাদা ছবি।',
  sectionRoomHint: 'রুমের ছয়টি আলাদা ছবি।',
  openCamera: 'ক্যামেরা খুলুন',
  capture: 'ছবি তুলুন',
  retake: 'আবার তুলুন',
  usePhoto: 'ছবি ব্যবহার করুন',
  uploading: 'আপলোড হচ্ছে…',
  retrying: 'আবার চেষ্টা হচ্ছে…',
  uploaded: 'আপলোড হয়েছে',
  cameraStarting: 'ক্যামেরা চালু হচ্ছে…',
  cameraDeniedTitle: 'ক্যামেরার অনুমতি দরকার',
  cameraDeniedText:
    'পরিদর্শনের প্রমাণ রাখতে ROOM VERIFY-এর ক্যামেরা দরকার। ব্রাউজার সেটিংসে ক্যামেরার অনুমতি দিয়ে আবার চেষ্টা করুন।',
  cameraMissingTitle: 'কোনো ক্যামেরা পাওয়া যায়নি',
  cameraMissingText: 'এই ডিভাইসে ব্যবহারযোগ্য ক্যামেরা নেই। পরিদর্শনের জন্য ক্যামেরা লাগবেই।',
  cameraInsecureTitle: 'নিরাপদ সংযোগ প্রয়োজন',
  cameraInsecureText: 'ক্যামেরা শুধু HTTPS-এ কাজ করে। অ্যাপটি https:// ঠিকানা দিয়ে খুলুন।',
  cameraUnavailableTitle: 'ক্যামেরা চালু হয়নি',
  cameraUnavailableText:
    'ক্যামেরা ঠিকমতো চালু হয়নি। ফোনে কল আসার পরে বা ফোন লক হওয়ার পরে এমন হতে পারে। আবার চেষ্টা করতে “ক্যামেরা আবার চালু করুন” চাপুন।',
  cameraRecovering: 'ক্যামেরা আবার চালু হচ্ছে…',
  restartCamera: 'ক্যামেরা আবার চালু করুন',
  photoSlot: (n) => `ছবি ${n}`,
  allPhotosDone: 'সব ছবি তোলা হয়েছে',
  nextSection: 'পরবর্তী সেকশন',
  reviewItems: 'রুম আইটেমে যান',

  roomItems: 'রুম আইটেম',
  roomItemsQuestion: 'রুমের সব আইটেম কি ঠিক আছে?',
  everythingComplete: 'সবকিছু ঠিক আছে',
  everythingCompleteHint: 'রুম থেকে কিছুই হারায়নি।',
  missingItems: 'হারানো আইটেম',
  missingItemsHint: 'কিছু একটা নেই — কোনটি বেছে নিন।',
  selectMissingItems: 'যা যা নেই সব বেছে নিন',
  otherItem: 'অন্যান্য',
  otherItemPlaceholder: 'অন্য কোনো হারানো আইটেম লিখুন',
  missingItemsEmpty: 'অন্তত একটি আইটেম বেছে নিন, অথবা অন্যান্য-তে লিখুন।',
  completeInspection: 'পরিদর্শন সম্পন্ন করুন',
  completing: 'সম্পন্ন হচ্ছে…',

  inspectionCompleted: 'পরিদর্শন সম্পন্ন হয়েছে',
  reportSent: 'রিপোর্ট ম্যানেজমেন্টে পাঠানো হয়েছে',
  checkAnotherRoom: 'আরেকটি রুম দেখুন',
  durationLabel: 'সময় লেগেছে',

  errSessionExpired: 'আপনার সেশন শেষ হয়ে গেছে',
  errSessionExpiredHelp: 'যেখানে ছিলেন সেখান থেকে চালিয়ে যেতে আবার সাইন ইন করুন।',
  errInvalidCredentials: 'ইউজারনেম বা পাসওয়ার্ড ভুল।',
  errNetwork: 'সংযোগে সমস্যা। ইন্টারনেট দেখে আবার চেষ্টা করুন।',
  errNetworkPhoto:
    'সংযোগ দুর্বল থাকায় ছবিটি আপলোড হয়নি। ছবিটি এই স্ক্রিনে সংরক্ষিত আছে — আবার চেষ্টা করতে "ছবি ব্যবহার করুন" চাপুন।',
  errServer: 'সার্ভারে সাময়িক সমস্যা হয়েছে। একটু পরে আবার চেষ্টা করুন।',
  errTooLarge: 'ছবিটি অনেক বড়। আবার তুলুন।',
  errUnsupported: 'এই ছবির ফরম্যাট সমর্থিত নয়। আবার ছবি তুলুন।',
  errLocked: 'এই পরিদর্শন আগেই সম্পন্ন হয়েছে।',
  errPhotosIncomplete: 'কিছু প্রয়োজনীয় ছবি এখনও বাকি আছে।',
  errForbidden: 'এই অ্যাকাউন্ট দিয়ে এটি করা যাবে না।',
  errUnknown: 'কিছু একটা ভুল হয়েছে। আবার চেষ্টা করুন।',
  signInAgain: 'আবার সাইন ইন করুন',
  sessionKeptSafe: 'আপনার পরিদর্শন সংরক্ষিত আছে। কিছুই হারায়নি।',
};

const DICTIONARIES: Record<Language, Copy> = { en, bn };

export const dictionary = (language: Language): Copy => DICTIONARIES[language];

const STORAGE_KEY = 'roomverify.language';

export function loadLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'bn') return stored;
  } catch {
    /* private mode or blocked storage — fall through to the default */
  }
  return navigator.language?.toLowerCase().startsWith('bn') ? 'bn' : 'en';
}

export function saveLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    /* not being able to remember the choice must not break the app */
  }
}

/** Maps an API error code to copy in the active language. */
export function errorMessage(copy: Copy, code: string | null): string {
  switch (code) {
    case 'SESSION_EXPIRED':
      return copy.errSessionExpired;
    case 'UNAUTHORIZED':
      return copy.errSessionExpired;
    case 'INVALID_CREDENTIALS':
      return copy.errInvalidCredentials;
    case 'FORBIDDEN':
      return copy.errForbidden;
    case 'NETWORK':
      return copy.errNetwork;
    case 'IMAGE_TOO_LARGE':
      return copy.errTooLarge;
    case 'UNSUPPORTED_IMAGE_TYPE':
      return copy.errUnsupported;
    case 'INSPECTION_LOCKED':
      return copy.errLocked;
    case 'PHOTOS_INCOMPLETE':
      return copy.errPhotosIncomplete;
    case 'MISSING_ITEMS_EMPTY':
      return copy.missingItemsEmpty;
    case 'PHOTO_STORAGE_FAILED':
    case 'INTERNAL_ERROR':
      return copy.errServer;
    default:
      return copy.errUnknown;
  }
}

export type { Copy };
